import { Modal, Radio } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { ExternalEventConfirmationData, ExternalEventConfirmationModalProps } from './utilities/TypeDefs';
import { translateLabel } from '../../modules/journey-canvas/utility/helpers';

/**
 * Represents a modal component for external event confirmation.
 * @name ExternalEventConfirmationModal
 * @param {ExternalEventConfirmationModalProps} props - The props for the ExternalEventConfirmationModal component.
 * @returns {JSX.Element} - The ExternalEventConfirmationModal component.
 */
export const ExternalEventConfirmationModal: React.FC<ExternalEventConfirmationModalProps> = ({ externalEventConfirmationData = undefined, show, onValueUpdated, onClose, readOnly = false }: ExternalEventConfirmationModalProps) => {
  const [externalValue, setExternalValue] = useState<boolean>(false);

  useEffect(() => {
    setExternalValue(externalEventConfirmationData?.external ?? false);
  }, [externalEventConfirmationData]);

  /**
 * Handles the save event of the modal.
 */
  const handleSaveModal = (): void => {
    const data: ExternalEventConfirmationData = {
      external: externalValue,
      toNodeId: externalEventConfirmationData?.toNodeId ?? '',
      fromNodeId: externalEventConfirmationData?.fromNodeId ?? ''
    };
    onValueUpdated(data);
  };

  /**
   * Handles the change event of the radio buttons.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event.
   */
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setExternalValue(value === 'true');
  };

  const modalBody = (
    <div data-testid="externalEventConfirmationModal">
      <p>
        {translateLabel('labels.external_event_confirmation_modal_description')}
      </p>
      <Radio
        disabled={readOnly}
        checked={externalValue}
        label={translateLabel('labels.external_event_confirmation_modal_true')}
        value="true"
        onChange={handleRadioChange}
      />
      <Radio
        disabled={readOnly}
        checked={!externalValue}
        label={translateLabel('labels.external_event_confirmation_modal_false')}
        value="false"
        onChange={handleRadioChange}
      />
    </div>
  );

  return (
    <Modal
      modalTitle={translateLabel('labels.external_event_confirmation_modal_header')}
      modalBody={modalBody}
      modalShow={show}
      btnSaveText={translateLabel('labels.external_event_confirmation_modal_save')}
      btnCloseText={translateLabel('labels.external_event_confirmation_modal_cancel')}
      onSaveModal={handleSaveModal}
      onCloseModal={onClose}
      alignToCentre
      disableCloseBtn={false}
      disableSaveBtn={readOnly}
    />
  );
};
